import { EventHandler, FederationEvents, Unsubscribe } from '../types/types';

export interface EventBus {
  emit<T extends keyof FederationEvents>(eventName: T, payload: FederationEvents[T]): void;
  on<T extends keyof FederationEvents>(eventName: T, handler: EventHandler<T>): Unsubscribe;
  off<T extends keyof FederationEvents>(eventName: T, handler: EventHandler<T>): void;
  once<T extends keyof FederationEvents>(eventName: T, handler: EventHandler<T>): void;
}

export class FederationEventBus implements EventBus {
  private listeners: Map<keyof FederationEvents, Set<Function>> = new Map();
  private debugMode: boolean = false;

  constructor(debug: boolean = false) {
    this.debugMode = debug;
  }

  get isDebugMode(): boolean {
    return this.debugMode;
  }

  emit<T extends keyof FederationEvents>(eventName: T, payload: FederationEvents[T]): void {
    if (this.debugMode) {
      console.log(`[FederationEventBus] Emitting event: ${String(eventName)}`, payload);
    }

    const eventListeners = this.listeners.get(eventName);
    if (eventListeners) {
      // Create a copy to avoid issues if listeners are removed during iteration
      const listenersArray = Array.from(eventListeners);
      listenersArray.forEach((listener) => {
        try {
          listener(payload);
        } catch (error) {
          console.error(`[FederationEventBus] Error in event listener for ${String(eventName)}:`, error);
        }
      });
    }
  }

  on<T extends keyof FederationEvents>(eventName: T, handler: EventHandler<T>): Unsubscribe {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }

    this.listeners.get(eventName)!.add(handler);

    if (this.debugMode) {
      console.log(`[FederationEventBus] Added listener for: ${String(eventName)}`);
    }

    // Return unsubscribe function
    return () => this.off(eventName, handler);
  }

  off<T extends keyof FederationEvents>(eventName: T, handler: EventHandler<T>): void {
    const eventListeners = this.listeners.get(eventName);
    if (eventListeners) {
      eventListeners.delete(handler);

      if (eventListeners.size === 0) {
        this.listeners.delete(eventName);
      }

      if (this.debugMode) {
        console.log(`[FederationEventBus] Removed listener for: ${String(eventName)}`);
      }
    }
  }

  once<T extends keyof FederationEvents>(eventName: T, handler: EventHandler<T>): void {
    const onceHandler = (payload: FederationEvents[T]) => {
      handler(payload);
      this.off(eventName, onceHandler as EventHandler<T>);
    };

    this.on(eventName, onceHandler as EventHandler<T>);
  }

  removeAllListeners(eventName?: keyof FederationEvents): void {
    if (eventName) {
      this.listeners.delete(eventName);
      if (this.debugMode) {
        console.log(`[FederationEventBus] Removed all listeners for: ${String(eventName)}`);
      }
    } else {
      this.listeners.clear();
      if (this.debugMode) {
        console.log('[FederationEventBus] Removed all listeners');
      }
    }
  }
}
